/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import omit from 'lodash/omit';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import IconAlertTriangle from '@cimpress-technology/react-streamline-icons/lib/IconAlertTriangle';
import IconBin from '@cimpress-technology/react-streamline-icons/lib/IconBin';
import IconCheckCircleAlt from '@cimpress-technology/react-streamline-icons/lib/IconCheckCircleAlt';
import IconInformationCircle from '@cimpress-technology/react-streamline-icons/lib/IconInformationCircle';
import SkuAttributeModelBuilder from '@cimpress-technology/sku-attribute-model-builder';
import { Alert, Accordion, Radio, RadioGroup } from '@cimpress/react-components';
import Button from '@cimpress/react-components/lib/Button';
import InlineEdit from '@cimpress/react-components/lib/InlineEdit';
import TextField from '@cimpress/react-components/lib/TextField';
import Tooltip from '@cimpress/react-components/lib/Tooltip';
import { danger } from '@cimpress/react-components/lib/colors';
import { info, success } from '@cimpress/react-components/lib/colors';

import auth from '../../auth';
import { compactCommaSeparatedString } from '../../helpers/compactCommaSeparatedString';
import { createStateFromPackCalculator } from '../../helpers/createStateFromMappingSet';
import useAttributeModel from '../../hooks/useAttributeModel';
import usePackCalculator from '../../hooks/usePackCalculator';
import usePackCalculatorsList from '../../hooks/usePackCalculatorsList';
import {
  Action,
  addPackCalculatorToList,
  editMapping,
  editPackCalculator,
  editPackCalculatorInList,
  moveMainPackCalculatorToList,
  rehydrateAttributeModel,
  rehydratePackCalculator,
  rehydratePackCalculatorList,
  removePackMapping,
  removePackCalculatorFromList,
} from '../../reducers/editActions';
import { BIGGEST_PACKS, EVEN_PACKS, createInitialPackCalculatorState } from '../../reducers/editConstants';
import { PackTestEvaluationResponse, ShreddingStrategy } from '../../services/types';
import Loading from '../Loading';
import ErrorDisplay from '../shared/ErrorDisplay';
import { MappingState, SelectField } from '../shared/types';
import DimensionInputs from './DimensionInputs';
import PackQuantitiesInputs from './PackQuantitiesInputs';
import WeightUnitSelect from './WeightUnitSelect';
import messages from './messages';

const accessToken = auth.getAccessToken();
const environment = process.env.REACT_APP_ENVIRONMENT_NAME;

const packCalculatorBoxStyle = {
  display: 'inline-block',
  marginTop: '15px',
  padding: '16px',
  border: '1px solid #e1e5e9',
  borderRadius: '0px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  width: 'fit-content',
};

const skuAttributeDisplay = css`
  position: relative;

  .sku-preview,
  form,
  span.label {
    display: none;
  }

  max-width: 700px;
`;

export type AttributeModelRef = {
  publish: () => Promise<any>;
};

const evaluationGrid = css`
  margin: 15px 0 15px 0;
  padding: 16px;
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(auto-fill, 50px);
  grid-auto-columns: max-content;
  column-gap: 15px;
  row-gap: 15px;
  align-items: center;
`;

type PackCalculatorMappingProps = {
  dispatch: React.Dispatch<Action>;
  mapping: MappingState;
  setRefs: React.Dispatch<React.SetStateAction<Record<string, AttributeModelRef | null>>>;
  id: string;
  evaluationResult?: PackTestEvaluationResponse;
  resetEvaluation: () => void;
  mappingsLoaded: Set<String>;
  setMappingsLoaded: (mappingsLoaded: Set<String>) => void;
};

const PackCalculatorMapping = ({
  dispatch,
  mapping,
  evaluationResult,
  resetEvaluation,
  setRefs,
  id,
  mappingsLoaded,
  setMappingsLoaded,
}: PackCalculatorMappingProps) => {
  const { packCalculator, packCalculatorList, id: mappingId } = mapping;
  const { formatMessage } = useIntl();

  const [commaSeparatedSkus, setCommaSeparatedSkus] = useState<string>(mapping.skus.join(','));
  const [weightUnitSelection, setWeightUnitSelection] = useState<SelectField>();

  const [selectorRef, setSelectorRef] = useState<AttributeModelRef | null>(null);

  // Don't use an actual ref, but a callback ref so we can actually know when the "ref" mutates.
  // Even though this is a stable callback, we still get the value --> null --> value as a ref;
  // not sure why though, possibly due to how the attribute selector is handling the ref, see: https://github.com/facebook/react/issues/9328#issuecomment-298438237
  const skuAttributeSelectorRef = useCallback(
    ref => {
      // Don't call setState if we already have a ref.
      // This would cause an infinite loop which is why we can't pass `setSelectorRef` directly as the "ref".
      // Have to do an extra null check for the incoming ref due to stated issue above.
      if (ref != null && selectorRef == null) {
        setSelectorRef(ref);
      }
    },
    [selectorRef],
  );

  // Keep track of all the refs from the attribute model component.
  // When this PackCalculatorMapping component gets unmounted, remove the ref from state so it doesn't get published
  useEffect(() => {
    setRefs((refs: any) => ({ ...refs, [id]: selectorRef }));
    return () => {
      setRefs(refs => omit(refs, id));
    };
  }, [id, setRefs, selectorRef]);

  const packCalculatorQuery = usePackCalculator({
    id: mapping.retrievedPackCalculatorId,
    onSuccess: data => {
      const newSet: Set<String> = new Set(mappingsLoaded);
      newSet.add(mapping.retrievedPackCalculatorId || '');
      setMappingsLoaded(newSet);
      dispatch(rehydratePackCalculator(createStateFromPackCalculator(data)));
    },
  });

  const packCalculatorListQuery = usePackCalculatorsList({
    ids: mapping.retrievedPackCalculatorListIds,
    onSuccess: data => {
      const packCalculatorList = data.map(calc => createStateFromPackCalculator(calc));
      dispatch(
        rehydratePackCalculatorList({
          mappingId,
          packCalculatorList,
        }),
      );
    },
  });

  const attributeModelQuery = useAttributeModel({
    attributeModelUrl: mapping.attributeModelUrl,
    onSuccess: (data: any) => dispatch(rehydrateAttributeModel({ mappingId, data })),
  });

  const onChangePackCalculator = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(editPackCalculator({ mappingId, field: name, value }));
    resetEvaluation();
  };

  // Strip extra commas and spaces from input
  const onBlurSkuInput = () => setCommaSeparatedSkus(compactCommaSeparatedString(commaSeparatedSkus).toString());

  const onChangeSkus = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    const skus = compactCommaSeparatedString(value);
    setCommaSeparatedSkus(value);
    dispatch(editMapping({ mappingId, field: name, value: skus }));
    resetEvaluation();
  };

  const onChangeWeightUnit = (selection: SelectField) => {
    setWeightUnitSelection(selection);
    dispatch(editPackCalculator({ mappingId, field: 'weightUnit', value: selection.value }));
    resetEvaluation();
  };

  const onDeleteCalculator = () => {
    dispatch(removePackMapping({ mappingId }));
    resetEvaluation();
  };

  const onChangeShreddingStrategy = (_: any, val: ShreddingStrategy) => {
    if (val === BIGGEST_PACKS) {
      dispatch(editPackCalculator({ mappingId, field: 'maxQuantity', value: '' }));
    } else {
      dispatch(editPackCalculator({ mappingId, field: 'packableQuantities', value: '' }));
      dispatch(editPackCalculator({ mappingId, field: 'preferredPackQuantity', value: '' }));
    }
    dispatch(editPackCalculator({ mappingId, field: 'shreddingStrategy', value: val }));
    resetEvaluation();
  };

  const onAddPackCalculator = () => {
    const newPackCalculator = createInitialPackCalculatorState();

    // Move main calculator to list if this is the first time creating a group
    if (!mapping.packCalculatorList) {
      dispatch(
        editMapping({
          mappingId,
          field: 'packGroupName',
          value: mapping.packCalculator.name,
        }),
      );

      // Use the new action to move main calculator to list
      dispatch(moveMainPackCalculatorToList({ mappingId }));

      // Rename the main calculator to "Pack" after moving it to the list
      dispatch(
        editPackCalculatorInList({
          mappingId,
          calculatorIndex: 0,
          field: 'name',
          value: 'Pack',
        }),
      );
    }
    newPackCalculator.name = `Pack`;
    dispatch(addPackCalculatorToList({ mappingId, packCalculator: newPackCalculator }));
  };

  if (packCalculatorQuery.isError) {
    return (
      <div style={{ margin: 'auto', marginTop: '30px' }}>
        <hr />
        <ErrorDisplay errorMsg={formatMessage(messages.errorMessage)} />
      </div>
    );
  }

  if (packCalculatorQuery.isLoading || packCalculatorListQuery.isLoading || attributeModelQuery.isLoading) {
    return <Loading />;
  }

  const header = (
    <div style={{ margin: '-15px 0' }}>
      {packCalculatorList ? (
        // Show pack group name when there's a list
        <InlineEdit
          size="h3"
          name="packGroupName"
          value={mapping.packGroupName || formatMessage(messages.packGroup)}
          onKeyDownCapture={e => e.key === ' ' && e.stopPropagation()}
          onClickCapture={e => e.stopPropagation()}
          onChange={({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
            dispatch(editMapping({ mappingId, field: name, value }));
          }}
        />
      ) : (
        // Show main pack calculator name when there's no list
        <InlineEdit
          size="h3"
          name="name"
          value={mapping.packCalculator.name}
          onKeyDownCapture={e => e.key === ' ' && e.stopPropagation()}
          onClickCapture={e => e.stopPropagation()}
          onChange={onChangePackCalculator}
        />
      )}
    </div>
  );

  return (
    <Accordion
      style={{ marginBottom: '10px' }}
      bodyStyle={{ padding: '16px' }}
      title={header}
      actions={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {Boolean(evaluationResult) && (
            <IconCheckCircleAlt
              weight="fill"
              size="lg"
              style={{ color: success.base, marginLeft: 'auto', marginRight: '5px' }}
            />
          )}
          {attributeModelQuery.isError && <IconAlertTriangle weight="fill" size="lg" style={{ color: danger.base }} />}
          <Button onClick={onDeleteCalculator} variant="link" icon={<IconBin size="lg" />} />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div css={skuAttributeDisplay}>
          <div style={{ display: 'flex' }}>
            <TextField
              style={{ flex: 1 }}
              name="skus"
              label={formatMessage(messages.commaSeparatedSkus)}
              value={commaSeparatedSkus}
              onChange={onChangeSkus}
              onBlur={onBlurSkuInput}
            />
            <Tooltip contents={formatMessage(messages.skuTooltip)}>
              <IconInformationCircle size="2x" style={{ margin: '10px 0 0 10px' }} weight="fill" color={info.base} />
            </Tooltip>
          </div>
          {attributeModelQuery.isError && (
            <Alert status="danger" message={formatMessage(messages.attributeModelFetchFailed)} />
          )}
          <SkuAttributeModelBuilder
            token={accessToken}
            mcpSkus={mapping.skus}
            isProduction={environment === 'Production'}
            ref={skuAttributeSelectorRef}
            showPublishBtn={false}
            attributeModel={mapping.attributeModel}
          />
        </div>
        <h4>Pack Information</h4>
        <RadioGroup
          onChange={onChangeShreddingStrategy}
          inline
          defaultSelected={BIGGEST_PACKS}
          name="shreddingStrategy"
          valueSelected={
            packCalculatorList && packCalculatorList.length > 0
              ? packCalculatorList[0]?.shreddingStrategy || BIGGEST_PACKS
              : packCalculator.shreddingStrategy
          }
        >
          <Radio label={formatMessage(messages.useBiggestPacks)} value={BIGGEST_PACKS} />
          <Radio label={formatMessage(messages.dividePacksEvenly)} value={EVEN_PACKS} />
        </RadioGroup>

        {!packCalculatorList && (
          <div style={{ display: 'flex', marginTop: '5px' }}>
            <div style={packCalculatorBoxStyle}>
              <div style={{ width: '700px' }}>
                <PackQuantitiesInputs
                  mappingId={mappingId}
                  packCalculator={packCalculator}
                  resetEvaluation={resetEvaluation}
                  dispatch={dispatch}
                />
                <div style={{ display: 'flex' }}>
                  <TextField
                    style={{ marginRight: '10px', width: '95%' }}
                    name="packsPerContainer"
                    label={formatMessage(messages.numberOfPacks)}
                    type="number"
                    value={packCalculator.packsPerContainer}
                    onChange={onChangePackCalculator}
                  />
                  <Tooltip contents={formatMessage(messages.packsPerContainerTooltip)}>
                    <IconInformationCircle size="2x" style={{ margin: '10px 0 0 0' }} weight="fill" color={info.base} />
                  </Tooltip>
                </div>
                <div style={{ display: 'flex' }}>
                  <TextField
                    style={{ marginRight: '10px', width: '80%' }}
                    name="weightFormula"
                    label={formatMessage(messages.weightFormula)}
                    value={packCalculator.weightFormula}
                    onChange={onChangePackCalculator}
                  />
                  <div style={{ width: '20%' }}>
                    <WeightUnitSelect
                      defaultWeight={packCalculator.weightUnit}
                      value={weightUnitSelection}
                      onChange={onChangeWeightUnit}
                    />
                  </div>
                </div>
                <DimensionInputs dispatch={dispatch} mapping={mapping} resetEvaluation={resetEvaluation} />
              </div>
            </div>
            {evaluationResult &&
              evaluationResult._embedded.item[0].selectedPacks
                .filter(pack => pack._embedded.pack.key === packCalculator.testKey)
                .map((pack, i) => (
                  <div key={i} css={evaluationGrid}>
                    <div style={{ gridRowStart: '1' }}>
                      {pack.quantity} packs of {pack._embedded.pack.quantity} each
                    </div>
                    <div style={{ gridRowStart: `${packCalculator.shreddingStrategy === EVEN_PACKS ? '3' : '4'}` }}>
                      {pack._embedded.pack.weight}
                    </div>
                    {pack._embedded.pack.preferredContainerType === 'tube' ? (
                      <>
                        <div style={{ gridRowStart: `${packCalculator.shreddingStrategy === EVEN_PACKS ? '5' : '6'}` }}>
                          {pack._embedded.pack.height}
                        </div>
                        <div>{pack._embedded.pack.diameter}</div>
                      </>
                    ) : (
                      <>
                        <div style={{ gridRowStart: `${packCalculator.shreddingStrategy === EVEN_PACKS ? '5' : '6'}` }}>
                          {pack._embedded.pack.length}
                        </div>
                        <div style={{ gridRowStart: `${packCalculator.shreddingStrategy === EVEN_PACKS ? '6' : '7'}` }}>
                          {pack._embedded.pack.width}
                        </div>
                        <div style={{ gridRowStart: `${packCalculator.shreddingStrategy === EVEN_PACKS ? '7' : '8'}` }}>
                          {pack._embedded.pack.height}
                        </div>
                      </>
                    )}
                  </div>
                ))}
          </div>
        )}

        {/* Render pack calculators from packCalculatorList if not empty */}
        {packCalculatorList && packCalculatorList.length > 0 && (
          <>
            {packCalculatorList.map((calculator, index) => (
              <div key={`pack-calculator-${calculator.id}-${index}`} style={{ display: 'flex', marginTop: '15px' }}>
                <div style={packCalculatorBoxStyle}>
                  <div style={{ width: '700px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        padding: '10px 15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef',
                      }}
                    >
                      <InlineEdit
                        value={calculator.name}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          const name = event.target.value;
                          dispatch(
                            editPackCalculatorInList({
                              mappingId,
                              calculatorIndex: index,
                              field: 'name',
                              value: name,
                            }),
                          );
                        }}
                        style={{ margin: 0, fontSize: '1.5rem', fontWeight: 500 }}
                      />
                      <Button
                        variant="link"
                        icon={<IconBin size="lg" />}
                        onClick={() => {
                          dispatch(removePackCalculatorFromList({ mappingId, calculatorIndex: index }));
                        }}
                      />
                    </div>
                    <PackQuantitiesInputs
                      mappingId={mappingId}
                      packCalculator={calculator}
                      resetEvaluation={resetEvaluation}
                      dispatch={dispatch}
                      calculatorIndex={index}
                    />
                    <div style={{ display: 'flex' }}>
                      <TextField
                        style={{ marginRight: '10px', width: '95%' }}
                        name="packsPerContainer"
                        label={formatMessage(messages.numberOfPacks)}
                        type="number"
                        value={calculator.packsPerContainer}
                        onChange={e => {
                          dispatch(
                            editPackCalculatorInList({
                              mappingId,
                              calculatorIndex: index,
                              field: 'packsPerContainer',
                              value: e.target.value,
                            }),
                          );
                        }}
                      />
                      <Tooltip contents={formatMessage(messages.packsPerContainerTooltip)}>
                        <IconInformationCircle
                          size="2x"
                          style={{ margin: '10px 0 0 0' }}
                          weight="fill"
                          color={info.base}
                        />
                      </Tooltip>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <TextField
                        style={{ marginRight: '10px', width: '80%' }}
                        name="weightFormula"
                        label={formatMessage(messages.weightFormula)}
                        value={calculator.weightFormula}
                        onChange={e => {
                          dispatch(
                            editPackCalculatorInList({
                              mappingId,
                              calculatorIndex: index,
                              field: 'weightFormula',
                              value: e.target.value,
                            }),
                          );
                        }}
                      />
                      <div style={{ width: '20%' }}>
                        <WeightUnitSelect
                          defaultWeight={calculator.weightUnit}
                          value={undefined}
                          onChange={(selection: SelectField) => {
                            dispatch(
                              editPackCalculatorInList({
                                mappingId,
                                calculatorIndex: index,
                                field: 'weightUnit',
                                value: selection.value,
                              }),
                            );
                          }}
                        />
                      </div>
                    </div>
                    <DimensionInputs
                      dispatch={dispatch}
                      mapping={{ ...mapping, packCalculator: calculator }}
                      resetEvaluation={resetEvaluation}
                      calculatorIndex={index as any}
                    />
                  </div>
                </div>
                {evaluationResult &&
                  evaluationResult._embedded.item[0].selectedPacks
                    .filter(pack => pack._embedded.pack.key === calculator.testKey)
                    .map((pack, i) => (
                      <div key={i} css={evaluationGrid}>
                        <div style={{ gridRowStart: '2' }}>
                          {pack.quantity} packs of {pack._embedded.pack.quantity} each
                        </div>
                        <div style={{ gridRowStart: `${calculator.shreddingStrategy === EVEN_PACKS ? '4' : '5'}` }}>
                          {pack._embedded.pack.weight}
                        </div>
                        {pack._embedded.pack.preferredContainerType === 'tube' ? (
                          <>
                            <div style={{ gridRowStart: `${calculator.shreddingStrategy === EVEN_PACKS ? '6' : '7'}` }}>
                              {pack._embedded.pack.height}
                            </div>
                            <div style={{ gridRowStart: `${calculator.shreddingStrategy === EVEN_PACKS ? '7' : '8'}` }}>
                              {pack._embedded.pack.diameter}
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ gridRowStart: `${calculator.shreddingStrategy === EVEN_PACKS ? '6' : '7'}` }}>
                              {pack._embedded.pack.length}
                            </div>
                            <div style={{ gridRowStart: `${calculator.shreddingStrategy === EVEN_PACKS ? '7' : '8'}` }}>
                              {pack._embedded.pack.width}
                            </div>
                            <div style={{ gridRowStart: `${calculator.shreddingStrategy === EVEN_PACKS ? '8' : '9'}` }}>
                              {pack._embedded.pack.height}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
              </div>
            ))}
          </>
        )}

        {/* Buttons section at bottom of accordion */}
        <div
          style={{
            marginTop: '20px',
            padding: '16px 0',
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
          }}
        >
          <Tooltip contents="Add new pack for same product variant">
            <Button onClick={onAddPackCalculator}>{formatMessage(messages.addPack)}</Button>
          </Tooltip>
        </div>
      </div>
    </Accordion>
  );
};

export default PackCalculatorMapping;
